/* eslint-disable @typescript-eslint/no-explicit-any */
import { LocalDate, LocalDateTime, LocalTime } from 'js-joda';
import { defaultMemoize } from 'reselect';

import { CalendarItem, WalkIn } from './calendarItems';

import * as BookingFilterUtils from 'utils/BookingFilterUtils';
import { AvailabilityParams } from 'actions/AvailabilityActions';
import { CalendarParty, DailyService } from 'types';
import { TableIdMap } from 'selectors/routes/calendar/calendarFloorplanSelector';
import { TicketGroupsByDateTime } from 'selectors/ticketGroupsByDateTimeSelector';
import {
  concat,
  filter,
  forEach,
  isEmpty,
  map,
  max,
  min,
  sortBy,
  toNumber,
  uniq,
} from 'es-toolkit/compat';
import DayTimeRange from 'utils/DayTimeRange';

interface ServicesForWeekday {
  [weekday: string]: DailyService[];
}

export const getServiceDateFromUrl = defaultMemoize(
  (currentPathName?: string): string | undefined => {
    const match = currentPathName
      ? currentPathName.match(/([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/)
      : undefined;
    return match && !isEmpty(match) ? match[0] : undefined;
  }
);

export const shouldReloadCalendar = function (
  date: LocalDate,
  prevDate: LocalDate,
  service: string[],
  prevService: string[],
  queryParams: any,
  prevQueryParams: any,
  newlyTainted: boolean
): boolean {
  const dateHasChanged = date.compareTo(prevDate) !== 0;
  const serviceHasChanged = service !== prevService;
  const queryParamsHaveChanged = !BookingFilterUtils.queryParamsAreEqual(
    queryParams,
    prevQueryParams
  );

  return dateHasChanged || serviceHasChanged || queryParamsHaveChanged || newlyTainted;
};

export const getServiceTimeRangeForTypeAndDay = function (
  servicesForWeekday: any,
  serviceType: string | number,
  day: LocalDate
) {
  const serviceTimeRange = DayTimeRange.create();
  const weekday = day.dayOfWeek().toString();
  const services = filter(servicesForWeekday[weekday], {
    serviceType,
  });
  if (services.length === 0) {
    return null;
  }
  services.forEach((service: any) => {
    serviceTimeRange.union(service.startTime, service.endTime);
  });
  return serviceTimeRange;
};

/*
 * Gets the starting service for the given type for a given day.
 */
export const getServiceStartDateTimeForTypeAndDay = function (
  servicesForWeekday: ServicesForWeekday,
  serviceType: string | number,
  day: LocalDate
): LocalDateTime | undefined {
  const serviceTimeRange = getServiceTimeRangeForTypeAndDay(servicesForWeekday, serviceType, day);
  if (!serviceTimeRange) {
    return undefined;
  }
  return day.atTime(serviceTimeRange.getMinLocalTime());
};

/*
 * Gets the starting service for the given type for a given day.
 */
export const getServiceEndDateTimeForTypeAndDay = function (
  servicesForWeekday: ServicesForWeekday,
  serviceType: string | number,
  day: LocalDate
): LocalDateTime | undefined {
  const serviceTimeRange = getServiceTimeRangeForTypeAndDay(servicesForWeekday, serviceType, day);
  if (!serviceTimeRange) {
    return undefined;
  }
  const businessDay = serviceTimeRange.endsAfterMidnight() ? day.plusDays(1) : day;
  return businessDay.atTime(serviceTimeRange.getMaxLocalTime());
};

/*
 * Gets the starting service for the given type for a given day.
 */
export const getServiceStartTimeForDay = function (
  servicesForWeekday: ServicesForWeekday,
  day: LocalDate
): LocalDateTime {
  const serviceTimeRange = DayTimeRange.create();
  const weekday = day.dayOfWeek().toString();
  servicesForWeekday[weekday].forEach((service) => {
    serviceTimeRange.union(service.startTime, service.endTime);
  });
  return day.atTime(serviceTimeRange.getMinLocalTime());
};

/*
 * Gets the ending service for the given type for a given day.
 */
export const getServiceEndTimeForDay = function (
  servicesForWeekday: ServicesForWeekday,
  day: LocalDate
): LocalDateTime {
  const serviceTimeRange = DayTimeRange.create();
  const weekday = day.dayOfWeek().toString();
  servicesForWeekday[weekday].forEach((service) => {
    serviceTimeRange.union(service.startTime, service.endTime);
  });
  const businessDay = serviceTimeRange.endsAfterMidnight() ? day.plusDays(1) : day;
  return businessDay.atTime(serviceTimeRange.getMaxLocalTime());
};

export interface DateTimeRange {
  startDateTime: LocalDateTime;
  endDateTime: LocalDateTime;
}

export function getStartAndEndDateTimeForService(
  servicesForWeekday: any,
  date: LocalDate,
  serviceType?: string | number
): DateTimeRange {
  let startForServiceType: LocalDateTime | undefined;
  let endForServiceType: LocalDateTime | undefined;
  if (serviceType) {
    startForServiceType = getServiceStartDateTimeForTypeAndDay(
      servicesForWeekday,
      serviceType,
      date
    );
    endForServiceType = getServiceEndDateTimeForTypeAndDay(servicesForWeekday, serviceType, date);
  }

  const startDateTime = startForServiceType || getServiceStartTimeForDay(servicesForWeekday, date);
  const endDateTime = endForServiceType || getServiceEndTimeForDay(servicesForWeekday, date);
  return { startDateTime, endDateTime };
}

export const fillAllTimes = function (
  dateTimeRanges: DateTimeRange[],
  parties: CalendarParty[],
  calendar: TicketGroupsByDateTime,
  selectedDate: LocalDate
): string[] {
  let dateTimes: string[] = [];

  const timeMin = selectedDate.atTime(LocalTime.of(4, 0)).toString();
  const timeMax = selectedDate.plusDays(1).atTime(LocalTime.of(3, 45)).toString();

  forEach(dateTimeRanges, (dateTimeRange) => {
    const { startDateTime, endDateTime } = dateTimeRange;

    let timeCursor = startDateTime;

    while (timeCursor && !timeCursor.isAfter(endDateTime)) {
      dateTimes.push(timeCursor.toString());
      timeCursor = timeCursor.plusMinutes(15);
    }
  });

  dateTimes = uniq(
    concat(
      dateTimes,
      map(parties, (party) => {
        return party.dateTime15MinuteInterval;
      }),
      Object.keys(calendar)
    )
  );

  dateTimes = sortBy(filter(dateTimes, (dateTime) => dateTime >= timeMin && dateTime <= timeMax));
  return dateTimes;
};

export const getShowAvailabilityPanelParams = (options?: {
  tablesById?: TableIdMap;
  selectedTableIds?: string[];
  selectedCalendarItem?: CalendarItem;
  selectedTableIdCalendarSquare?: number;
  date?: LocalDate;
  ticketTypeIds?: number[];
}): AvailabilityParams => {
  const params: AvailabilityParams = {};

  if (!options) {
    return params;
  }

  const {
    date,
    tablesById,
    selectedTableIds: selectedTableId,
    selectedCalendarItem,
    selectedTableIdCalendarSquare,
    ticketTypeIds = [],
  } = options;

  const selectedTableIds = selectedTableId ? selectedTableId.map(Number) : [];

  if (selectedTableId && tablesById) {
    const tableId = toNumber(selectedTableId);
    const table = tablesById[tableId];
    if (table) {
      return { size: table.size, tableId: [tableId], date, ticketTypeId: ticketTypeIds };
    }
  }

  if (selectedCalendarItem && tablesById) {
    const tableId = toNumber(selectedTableIdCalendarSquare);
    const tableSize: number | undefined = tablesById?.[tableId]?.size;

    let paramSize: number | undefined;

    if (selectedCalendarItem.state === 'SOLD' && !selectedCalendarItem.purchase) {
      paramSize = min([
        (selectedCalendarItem as WalkIn).party.partySize,
        selectedCalendarItem.numBookable,
        tableSize,
      ]);

      return {
        size: max([1, paramSize]),
        date,
        ticketTypeId: ticketTypeIds,
      };
    }

    const ticketTypeId = isEmpty(ticketTypeIds)
      ? selectedCalendarItem.ticketGroup.ticketType[0].id
      : ticketTypeIds;
    paramSize = min([
      selectedCalendarItem.ticketGroup.maxPurchaseSize,
      selectedCalendarItem.numBookable,
      tableSize,
    ]);

    return {
      ticketTypeId: [ticketTypeId as number],
      size: max([1, paramSize]),
      date,
      tableId: selectedTableIds,
    };
  }

  if (date) {
    return { date, ticketTypeId: ticketTypeIds, tableId: selectedTableIds };
  }

  return params;
};
